import { nanoid } from "nanoid"
import Logger from "~/utils/Logger"

export async function useTypesenseJobCounts() {
  const { $api } = useNuxtApp()
  const { t } = useI18n()
  const { tenantId, jobShopId } = useHeaderStore()
  const { data: typesenseApiKey } = useNuxtData(`typesenseApiKey-${jobShopId}`)
  const { public: { typesenseUrl } } = useRuntimeConfig()
  const { data: jobShop } = useNuxtData("jobShopData")
  const jobShopLocale = computed(() => jobShop.value?.locale)

  const requestId = nanoid()
  const contextString = `TypeSense counters for tenantId: "${tenantId}", jobShopId: "${jobShopId}"`

  let startTime
  if (import.meta.server) {
    Logger(requestId).start(`Start fetching ${contextString}`)
    startTime = Date.now()
  }

  if (!typesenseApiKey.value)
    throw new Error("Typesense API key is missing")

  const { data, status: typesenseJobCountsStatus, error: typesenseJobCountsError } = await useAsyncData("typesenseJobCounts", () => $api.typesense.getJobCount(tenantId, jobShopId, typesenseApiKey.value, typesenseUrl), {
    transform: (rawData) => {
      const count = rawData?.results?.[0]?.found ?? 0
      const categories = (rawData?.results?.[0]?.facet_counts?.[0]?.counts ?? []).map((jobCount: any) => ({
        ...jobCount,
        jobsText: t("module.jobs-text", jobCount.count, { locale: jobShopLocale.value }),
      }))
      return {
        jobsCountAll: t("module.jobs-text", count, { locale: jobShopLocale.value }),
        jobsCountCategories: categories,
      }
    },
  })

  if (import.meta.server) {
    if (!typesenseJobCountsError.value) {
      const duration = Date.now() - startTime
      Logger(requestId).success(`Successfully fetched ${contextString}. (${duration}ms)`)
    }
    else {
      const errorMessage = typesenseJobCountsError.value?.data?.errors?.[0]?.message || typesenseJobCountsError.value?.data?.description || "Unknown error occurred"
      Logger(requestId).error(`Error while fetching ${contextString}: ${errorMessage}\n\n`, typesenseJobCountsError.value)
    }
  }

  return {
    jobsCountAll: computed(() => data.value?.jobsCountAll ?? ""),
    jobsCountCategories: computed(() => data.value?.jobsCountCategories ?? []),
    typesenseJobCountsStatus,
    typesenseJobCountsError,
  }
}
